import { useEffect, useState } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { useExperience } from '@/lib/experience';
import { LOGO, HERO_PHOTO } from '@/data/site';
import { Paw, Heart } from './brand/Shapes';

const STEPS = 6;

/**
 * Abertura fofa: o logo quicando, patinhas andando até 100% e uma cortina azul
 * que sobe arredondada revelando a página.
 */
export const Preloader = () => {
  const { setReady } = useExperience();
  const reduce = useReducedMotion();
  const [progress, setProgress] = useState(0);
  const [done, setDone] = useState(false);

  useEffect(() => {
    let seen = false;
    try {
      seen = sessionStorage.getItem('cp-intro') === '1';
      sessionStorage.setItem('cp-intro', '1');
    } catch (e) {
      /* storage indisponível */
    }
    const minTime = reduce ? 150 : seen ? 700 : 1900;
    const start = performance.now();

    const img = new Image();
    img.src = HERO_PHOTO.lg;
    let loaded = false;
    Promise.race([
      Promise.all([
        document.fonts ? document.fonts.ready : Promise.resolve(),
        img.decode ? img.decode().catch(() => {}) : Promise.resolve(),
      ]),
      new Promise((r) => setTimeout(r, 3500)),
    ]).then(() => {
      loaded = true;
    });

    let frame;
    const tick = () => {
      const t = Math.min(1, (performance.now() - start) / minTime);
      const eased = 1 - Math.pow(1 - t, 3);
      const value = loaded ? eased : Math.min(eased, 0.9);
      setProgress(value);
      if (value >= 1) {
        setDone(true);
        setReady(true);
        return;
      }
      frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [reduce, setReady]);

  const pct = Math.round(progress * 100);
  const pawsShown = Math.round(progress * STEPS);

  return (
    <AnimatePresence>
      {!done && (
        <motion.div
          key="preloader"
          className="fixed inset-0 z-[200] flex flex-col items-center justify-center bg-pet-blue text-white overflow-hidden"
          initial={{ clipPath: 'ellipse(150% 150% at 50% 0%)' }}
          exit={{ clipPath: 'ellipse(150% 0% at 50% 0%)' }}
          transition={{ duration: 0.9, ease: [0.65, 0, 0.35, 1] }}
          aria-live="polite"
          aria-label={`Carregando ${pct}%`}
        >
          {/* coraçõezinhos flutuando no fundo */}
          {[...Array(8)].map((_, i) => (
            <motion.span
              key={i}
              className="absolute text-white/20"
              style={{ left: `${8 + i * 12}%`, bottom: '-10%', width: 18 + (i % 3) * 10 }}
              animate={{ y: ['0vh', '-120vh'], rotate: [0, i % 2 ? 30 : -30] }}
              transition={{ duration: 6 + (i % 4), repeat: Infinity, delay: i * 0.4, ease: 'linear' }}
            >
              <Heart className="w-full" />
            </motion.span>
          ))}

          <motion.div
            initial={{ scale: 0, rotate: -25 }}
            animate={{ scale: 1, rotate: 0 }}
            transition={{ type: 'spring', stiffness: 260, damping: 12 }}
          >
            <motion.img
              src={LOGO}
              alt=""
              className="w-32 h-32 md:w-36 md:h-36 rounded-full ring-[6px] ring-white shadow-2xl"
              animate={{ y: [0, -14, 0], scaleY: [1, 1.04, 0.96, 1] }}
              transition={{ duration: 0.9, repeat: Infinity, ease: 'easeInOut' }}
            />
          </motion.div>

          <p className="mt-8 font-display text-2xl md:text-3xl">Preparando tudo com carinho…</p>

          <div className="mt-6 flex items-end gap-3 md:gap-4 h-8" aria-hidden="true">
            {Array.from({ length: STEPS }).map((_, i) => (
              <motion.span
                key={i}
                style={{ marginBottom: i % 2 ? 10 : 0, rotate: i % 2 ? 18 : -18 }}
                initial={false}
                animate={{ opacity: i < pawsShown ? 1 : 0.25, scale: i < pawsShown ? 1 : 0.6 }}
                transition={{ type: 'spring', stiffness: 600, damping: 12 }}
              >
                <Paw className="w-6 h-6 text-white" />
              </motion.span>
            ))}
          </div>

          <span className="absolute bottom-6 right-6 md:bottom-10 md:right-10 font-display text-6xl md:text-8xl leading-none tabular-nums text-white/90">
            {pct}
            <span className="text-3xl md:text-5xl">%</span>
          </span>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default Preloader;
